import React, { useEffect, useState, useContext } from 'react';
import { Input, Select, Card, Spin, Button, Tag, Empty, Pagination, message, Tooltip } from 'antd';
import { SearchOutlined, DownloadOutlined, FileTextOutlined, PlayCircleOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { Context } from '../context';
import { Head } from './Head';
import { Flag, getLanguageName } from '../utils';

const API_URL = process.env.REACT_APP_API_URL || 'https://translation-manag-server.dev.torsunov.pro';

const LANGUAGES = [
  { value: null, label: 'Все языки' },
  { value: 'ru', label: 'Русский' },
  { value: 'eng', label: 'English' },
  { value: 'es', label: 'Espa\u00f1ol' },
  { value: 'de', label: 'Deutsch' },
  { value: 'kz', label: '\u049a\u0430\u0437\u0430\u049b' },
  { value: 'zh', label: '\u4e2d\u6587' },
  { value: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629' },
];

// Map between internal lang IDs and API lang codes
const langToApiCode = (langId) => {
  const map = { 'rus': 'ru', 'eng': 'en' };
  return map[langId] || langId;
};

const parseSRT = (srtText) => {
  if (!srtText) return [];
  const blocks = srtText.trim().split(/\n\s*\n/);
  return blocks.map(block => {
    const lines = block.trim().split('\n');
    if (lines.length < 3) return null;
    const timeLine = lines[1];
    const text = lines.slice(2).join('\n');
    const match = timeLine.match(/(\d{2}:\d{2}:\d{2}[,.]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[,.]\d{3})/);
    if (!match) return null;
    return {
      index: parseInt(lines[0]),
      start: match[1].replace(',', '.'),
      end: match[2].replace(',', '.'),
      text,
    };
  }).filter(Boolean);
};

const formatTime = (timeStr) => {
  // "00:01:23.456" -> "1:23"
  const parts = timeStr.split(':');
  const h = parseInt(parts[0]);
  const m = parseInt(parts[1]);
  const s = parts[2].split('.')[0];
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s}`;
  return `${m}:${s}`;
};

export function SubtitlesPage({ onLogout }) {
  const { fetchProjectsWithFilters } = useContext(Context);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLang, setSelectedLang] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 12;

  // Selected project for viewing subtitles
  const [selectedProject, setSelectedProject] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);

  // Subtitle viewer state
  const [viewingSubs, setViewingSubs] = useState(null); // {projectId, lang, folderNum, fileName}
  const [subsContent, setSubsContent] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('auth_token') || '';

  const loadProjects = async (pageNum = 1, search = searchQuery) => {
    setLoading(true);
    try {
      const filters = {};
      if (search) filters.name = search;

      const result = await fetchProjectsWithFilters(pageNum, pageSize, filters, 'file_updated_at', 'desc', false);
      setProjects(result.items || []);
      setTotalPages(result.total_pages || 0);
      setTotal(result.total || 0);
      setPage(result.page || 1);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects(1, '');
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
    loadProjects(1, value);
  };

  const handlePageChange = (newPage) => {
    loadProjects(newPage, searchQuery);
  };

  const openProject = async (project) => {
    setSelectedProject(project);
    setLinksLoading(true);
    setDownloadLinks(null);
    try {
      const langParam = selectedLang ? `?lang=${langToApiCode(selectedLang)}` : '';
      const response = await fetch(`${API_URL}/projects/${project.id}/download_links${langParam}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDownloadLinks(data);
      } else {
        message.error('Не удалось загрузить данные о субтитрах');
      }
    } catch (error) {
      console.error('Error fetching download links:', error);
      message.error('Ошибка загрузки');
    } finally {
      setLinksLoading(false);
    }
  };

  const viewSubtitles = async (projectId, lang, folderNum, fileName) => {
    setViewingSubs({ projectId, lang, folderNum, fileName });
    setSubsLoading(true);
    setSubsContent(null);
    try {
      const apiLang = langToApiCode(lang);
      const response = await fetch(
        `${API_URL}/projects/${projectId}/subtitles_content?lang=${apiLang}&folder_num=${folderNum}`,
        {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        }
      );
      if (response.ok) {
        const text = await response.text();
        setSubsContent(text);
      } else {
        message.error('Файл субтитров не найден');
        setViewingSubs(null);
      }
    } catch (error) {
      console.error('Error loading subtitles:', error);
      message.error('Ошибка загрузки субтитров');
      setViewingSubs(null);
    } finally {
      setSubsLoading(false);
    }
  };

  const closeSubtitles = () => {
    setViewingSubs(null);
    setSubsContent(null);
  };

  const closeProject = () => {
    setSelectedProject(null);
    setDownloadLinks(null);
    setViewingSubs(null);
    setSubsContent(null);
  };

  // Group subtitle files by language
  const getSubtitlesByLang = () => {
    if (!downloadLinks) return {};
    const groups = {};

    const addToGroup = (items, type) => {
      if (!items) return;
      items.forEach(item => {
        const lang = item.language || 'ru';
        if (!groups[lang]) groups[lang] = [];
        groups[lang].push({ ...item, type });
      });
    };

    addToGroup(downloadLinks.rus_subs, 'subs');
    addToGroup(downloadLinks.translated_subs, 'subs');
    addToGroup(downloadLinks.translated_subs_no_pauses, 'subs_no_pauses');
    addToGroup(downloadLinks.full_subs, 'full_subs');

    return groups;
  };

  // Get video files grouped by language
  const getVideosByLang = () => {
    if (!downloadLinks) return {};
    const groups = {};

    const addToGroup = (items, type) => {
      if (!items) return;
      items.forEach(item => {
        const lang = item.language || 'ru';
        if (!groups[lang]) groups[lang] = [];
        groups[lang].push({ ...item, type });
      });
    };

    addToGroup(downloadLinks.rus_video, 'video');
    addToGroup(downloadLinks.translated_video, 'video');
    addToGroup(downloadLinks.translated_video_no_pauses, 'video_no_pauses');
    addToGroup(downloadLinks.full_video, 'full_video');

    return groups;
  };

  const renderSubtitleViewer = () => {
    if (!viewingSubs) return null;

    const parsed = subsContent ? parseSRT(subsContent) : [];

    return (
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined />
            <span>{viewingSubs.fileName}</span>
            <Tag color="blue">{viewingSubs.lang}</Tag>
            {viewingSubs.folderNum && <Tag>Part {viewingSubs.folderNum}</Tag>}
          </div>
        }
        extra={<Button type="text" icon={<CloseOutlined />} onClick={closeSubtitles} />}
        style={{ marginTop: '16px' }}
      >
        {subsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            {parsed.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f0f0f0', position: 'sticky', top: 0, background: '#fff' }}>
                    <th style={{ padding: '8px', textAlign: 'left', width: '60px', color: '#999' }}>#</th>
                    <th style={{ padding: '8px', textAlign: 'left', width: '140px', color: '#999' }}>Time</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: '#999' }}>Text</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((entry, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', color: '#bbb', fontSize: '12px' }}>{entry.index}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '12px', color: '#1890ff', whiteSpace: 'nowrap' }}>
                        {formatTime(entry.start)} - {formatTime(entry.end)}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '13px', lineHeight: '1.5' }}>
                        {entry.text}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '13px', maxHeight: '400px', overflow: 'auto' }}>
                {subsContent}
              </pre>
            )}
          </div>
        )}
      </Card>
    );
  };

  const renderProjectDetail = () => {
    if (!selectedProject) return null;

    const subtitlesByLang = getSubtitlesByLang();
    const videosByLang = getVideosByLang();
    const allLangs = [...new Set([...Object.keys(subtitlesByLang), ...Object.keys(videosByLang)])].sort();

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Button onClick={closeProject} type="text" style={{ padding: '4px 8px' }}>
            &larr; Назад
          </Button>
          <h2 style={{ margin: 0 }}>{selectedProject.name}</h2>
          <Tag color="blue">{selectedProject.id}</Tag>
        </div>

        {linksLoading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><Spin size="large" /><div style={{ marginTop: '12px' }}>Загрузка файлов...</div></div>
        ) : downloadLinks && allLangs.length > 0 ? (
          <div>
            {allLangs.map(lang => {
              const subs = subtitlesByLang[lang] || [];
              const videos = videosByLang[lang] || [];
              if (subs.length === 0 && videos.length === 0) return null;

              // Filter by selected language if applicable
              if (selectedLang && langToApiCode(selectedLang) !== lang && lang !== selectedLang) return null;

              return (
                <Card
                  key={lang}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Flag langId={lang} height={18} />
                      <span>{getLanguageName(lang) || lang.toUpperCase()}</span>
                      <Tag>{subs.length} sub{subs.length !== 1 ? 's' : ''}</Tag>
                      {videos.length > 0 && <Tag color="blue">{videos.length} video</Tag>}
                    </div>
                  }
                  style={{ marginBottom: '12px' }}
                  size="small"
                >
                  {/* Subtitles */}
                  {subs.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: '#595959' }}>
                        <FileTextOutlined /> Субтитры
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {subs
                          .sort((a, b) => (a.folder_num || 0) - (b.folder_num || 0))
                          .map((sub, i) => (
                          <div
                            key={i}
                            style={{
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: '#fafafa',
                            }}
                          >
                            <span style={{ fontSize: '12px', color: '#595959' }}>
                              {sub.type === 'full_subs' ? 'Full' : sub.folder_num ? `Part ${sub.folder_num}` : sub.file_name}
                            </span>
                            {sub.type === 'subs_no_pauses' && (
                              <Tag color="orange" style={{ fontSize: '10px', margin: 0, lineHeight: '16px', padding: '0 4px' }}>no pauses</Tag>
                            )}
                            <Tooltip title="Download .srt">
                              <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                href={`${API_URL}/projects/${selectedProject.id}/download/${sub.download_url?.split('/download/')[1] || sub.file_name}`}
                                target="_blank"
                              />
                            </Tooltip>
                            {sub.folder_num && sub.type === 'subs' && (
                              <Tooltip title="View subtitles">
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => viewSubtitles(selectedProject.id, lang, sub.folder_num, sub.file_name)}
                                />
                              </Tooltip>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {videos.length > 0 && (
                    <div style={{ marginTop: subs.length > 0 ? '16px' : 0 }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px', color: '#595959' }}>
                        <PlayCircleOutlined /> Видео
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {videos
                          .sort((a, b) => (a.folder_num || 0) - (b.folder_num || 0))
                          .map((vid, i) => (
                          <div
                            key={i}
                            style={{
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: '#f0f5ff',
                            }}
                          >
                            <span style={{ fontSize: '12px', color: '#595959' }}>
                              {vid.type === 'full_video' ? 'Full video' : vid.folder_num ? `Part ${vid.folder_num}` : vid.file_name}
                            </span>
                            {vid.type === 'video_no_pauses' && (
                              <Tag color="orange" style={{ fontSize: '10px', margin: 0, lineHeight: '16px', padding: '0 4px' }}>no pauses</Tag>
                            )}
                            <Tooltip title="Download .mp4">
                              <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                href={`${API_URL}/projects/${selectedProject.id}/download/${vid.download_url?.split('/download/')[1] || vid.file_name}`}
                                target="_blank"
                              />
                            </Tooltip>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <Empty description="Нет доступных субтитров или видео для этого проекта" />
        )}

        {renderSubtitleViewer()}
      </div>
    );
  };

  const renderProjectsList = () => {
    return (
      <div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="Поиск по названию проекта..."
            allowClear
            onSearch={handleSearch}
            style={{ maxWidth: '400px', flex: 1 }}
            prefix={<SearchOutlined />}
            defaultValue={searchQuery}
          />
          <Select
            value={selectedLang}
            onChange={setSelectedLang}
            style={{ width: '160px' }}
            options={LANGUAGES}
            placeholder="Язык"
          />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><Spin size="large" /></div>
        ) : projects.length > 0 ? (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '12px',
            }}>
              {projects.map(project => (
                <Card
                  key={project.id}
                  hoverable
                  onClick={() => openProject(project)}
                  size="small"
                  style={{ cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontWeight: 500, fontSize: '14px', lineHeight: '1.4' }}>
                      {project.name || project.file_video_title || project.id}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Tag style={{ fontSize: '11px', margin: 0 }}>{project.id?.substring(0, 8)}...</Tag>
                      {project.parts_count > 0 && (
                        <Tag color="blue" style={{ fontSize: '11px', margin: 0 }}>{project.parts_count} parts</Tag>
                      )}
                      {project.available_languages && project.available_languages.length > 0 && (
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {project.available_languages.slice(0, 5).map(lang => (
                            <Flag key={lang} langId={lang} height={12} />
                          ))}
                          {project.available_languages.length > 5 && (
                            <span style={{ fontSize: '11px', color: '#999' }}>+{project.available_languages.length - 5}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Pagination
                  current={page}
                  total={total}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showTotal={(t) => `${t} projects`}
                />
              </div>
            )}
          </>
        ) : (
          <Empty description="Проекты не найдены" />
        )}
      </div>
    );
  };

  return (
    <>
      <Head activeTab="subtitles" onLogout={onLogout} />

      <div style={{ padding: '16px 24px', flexGrow: 1, background: '#f5f5f5', minHeight: 'calc(100vh - 60px)' }}>
        <h2 style={{ marginBottom: '16px' }}>Субтитры и видео</h2>
        {selectedProject ? renderProjectDetail() : renderProjectsList()}
      </div>
    </>
  );
}
